import React, { useEffect, useState, useCallback, useRef } from "react";
import "./ViewStocktakeForm.css";

const POLL_INTERVAL = 5000; // 5s real-time polling

const STATUS_COLORS = {
  completed: "#22c55e",
  discrepancy: "#ef4444",
  in_progress: "#f59e0b",
  finalized: "#6366f1",
  paused: "#94a3b8",
  cancelled: "#94a3b8",
};

const FINISHED_STATUSES = ["finalized", "completed", "discrepancy", "cancelled"];

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ViewStocktakeForm = ({ selectedSession, onClose }) => {
  const { session_id, name, location_name, scope, status, started_at, ended_at } = selectedSession;

  const [lines, setLines]               = useState([]);
  const [filterLocation, setFilterLocation] = useState("");
  const [filterType, setFilterType]     = useState("");       // "PO" | "FOC" | ""
  const [locations, setLocations]       = useState([]);
  const [scanInput, setScanInput]       = useState("");
  const [sessionStatus, setSessionStatus] = useState(status);
  const [summary, setSummary]           = useState(null);
  const [completing, setCompleting]     = useState(false);
  const [scanError, setScanError]       = useState("");
  const [scanSuccess, setScanSuccess]   = useState("");
  const [completeError, setCompleteError] = useState("");
  const pollRef = useRef(null);
  const scanInputRef = useRef(null);

  const isFinished = FINISHED_STATUSES.includes(sessionStatus);

  // ── Fetch lines ──────────────────────────────────────────────────────────────
  const fetchLines = useCallback(() => {
    if (!session_id) return;
    const params = new URLSearchParams({ pageSize: 200 });
    if (filterLocation) params.set("location_id", filterLocation);
    if (filterType)     params.set("type", filterType);

    fetch(`/api/stocktake/sessions/${session_id}/lines?${params}`, {
      headers: { ...authHeaders() },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to fetch lines: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data.rows)) setLines(data.rows);
      })
      .catch(console.error);
  }, [session_id, filterLocation, filterType]);

  // ── Fetch summary ─────────────────────────────────────────────────────────
  const fetchSummary = useCallback(() => {
    if (!session_id) return;
    fetch(`/api/stocktake/sessions/${session_id}/summary`, {
      headers: { ...authHeaders() },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to fetch summary: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (data.header) {
          // resolve_status (set by "Complete Stocktake") takes precedence over
          // the raw session status ("finalized"), otherwise the badge flips
          // back from COMPLETED/DISCREPANCY to FINALIZED on the next poll.
          setSessionStatus(data.header.resolve_status || data.header.status);
        }
        if (data.aggregate) setSummary(data.aggregate);
      })
      .catch(console.error);
  }, [session_id]);

  // ── Fetch locations for filter dropdown ─────────────────────────────────
  useEffect(() => {
    fetch("/api/locations", { headers: { ...authHeaders() } })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch locations");
        return r.json();
      })
      .then(setLocations)
      .catch(console.error);
  }, []);

  // ── Initial fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    fetchLines();
    fetchSummary();
  }, [fetchLines, fetchSummary]);

  // ── Polling — stops once the session is finished so a completed/discrepancy
  //    badge can't get overwritten by a stale in-flight poll ──────────────────
  useEffect(() => {
    if (isFinished) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(() => {
      fetchLines();
      fetchSummary();
    }, POLL_INTERVAL);

    return () => clearInterval(pollRef.current);
  }, [isFinished, fetchLines, fetchSummary]);

  // ── Scan handler ─────────────────────────────────────────────────────────
  const handleScan = async (e) => {
    e.preventDefault();
    const serial = scanInput.trim();
    if (!serial) return;

    setScanError("");
    setScanSuccess("");

    try {
      const res = await fetch(`/api/stocktake/sessions/${session_id}/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ serial_number: serial, qty: 1 }),
      });
      const data = await res.json();

      if (!res.ok) {
        setScanError(data.error || "Scan failed");
      } else {
        setScanSuccess(`✓ Scanned: ${serial}`);
        setScanInput("");
        // Immediately refresh lines so the row updates without waiting for poll
        fetchLines();
        fetchSummary();
      }
    } catch {
      setScanError("Network error — please try again.");
    }

    setTimeout(() => { setScanError(""); setScanSuccess(""); }, 3000);
    scanInputRef.current?.focus();
  };

  // ── Complete stocktake ───────────────────────────────────────────────────
  const handleComplete = async () => {
    setCompleting(true);
    setCompleteError("");
    try {
      // 1. Recalculate counted_qty from scans
      const endRes = await fetch(`/api/stocktake/sessions/${session_id}/endstocktake`, {
        method: "POST",
        headers: { ...authHeaders() },
      });
      if (!endRes.ok) throw new Error(`Failed to recalculate counts (${endRes.status})`);

      // 2. Re-fetch lines to check for discrepancies
      const linesRes = await fetch(`/api/stocktake/sessions/${session_id}/lines?pageSize=200`, {
        headers: { ...authHeaders() },
      });
      if (!linesRes.ok) throw new Error(`Failed to re-fetch lines (${linesRes.status})`);
      const data = await linesRes.json();
      const rows = data.rows || [];

      const hasDiscrepancy = rows.some((row) => {
        const variance = (row.counted_qty ?? 0) - (row.expected_qty ?? 0);
        return variance !== 0;
      });

      // 3. Finalize the session
      const finalizeRes = await fetch(`/api/stocktake/sessions/${session_id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ action: "finalize" }),
      });
      if (!finalizeRes.ok) throw new Error(`Failed to finalize session (${finalizeRes.status})`);

      // 4. Set custom status label
      const finalStatus = hasDiscrepancy ? "discrepancy" : "completed";
      const resolveRes = await fetch(`/api/stocktake/sessions/${session_id}/resolve`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ resolve_status: finalStatus }),
      });
      if (!resolveRes.ok) throw new Error(`Failed to set resolve status (${resolveRes.status})`);

      setSessionStatus(finalStatus);
      setLines(rows);
      fetchSummary();
    } catch (err) {
      console.error("Error completing stocktake:", err);
      setCompleteError(err.message || "Failed to complete stocktake. Please try again.");
    } finally {
      setCompleting(false);
    }
  };

  // ── Variance display helpers ─────────────────────────────────────────────
  const getVarianceDisplay = (row) => {
    // Before any scans: counted_qty === 0 and it was never scanned → variance = -1 sentinel
    if (row.counted_qty === 0 && row.expected_qty > 0) return { value: -1, label: "−1", cls: "variance-missing" };
    const v = (row.counted_qty ?? 0) - (row.expected_qty ?? 0);
    if (v === 0) return { value: 0, label: "0", cls: "variance-ok" };
    if (v > 0)   return { value: v, label: `+${v}`, cls: "variance-over" };
    return         { value: v, label: `${v}`,  cls: "variance-under" };
  };

  // ── Filtered lines ───────────────────────────────────────────────────────
  // NOTE: was reading row.type, but the API returns row.inventory_type —
  // this mismatch meant selecting anything other than "All Types" always
  // emptied the table, and the Type column always rendered "—".
  const displayLines = lines.filter((row) => {
    if (filterLocation && String(row.location_id) !== String(filterLocation)) return false;
    if (filterType && row.inventory_type !== filterType) return false;
    return true;
  });

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-content--wide">
        <button className="close-button" onClick={onClose}>&times;</button>

        {/* ── Header ── */}
        <div className="stkview-header">
          <div>
            <h3 className="stkview-title">{name}</h3>
            <p className="stkview-sub">
              {location_name && <span>{location_name} · </span>}
              <span>{scope}</span>
            </p>
          </div>
          <span
            className="stkview-badge"
            style={{ background: STATUS_COLORS[sessionStatus] || "#64748b" }}
          >
            {sessionStatus?.replace("_", " ").toUpperCase()}
          </span>
        </div>

        {/* ── Meta row ── */}
        <div className="stkview-meta">
          <div><span>Started</span><strong>{started_at ? new Date(started_at).toLocaleString() : "—"}</strong></div>
          <div><span>Ended</span><strong>{ended_at ? new Date(ended_at).toLocaleString() : "—"}</strong></div>
          {summary && (
            <>
              <div><span>Lines</span><strong>{summary.line_count}</strong></div>
              <div><span>Expected</span><strong>{summary.expected_total}</strong></div>
              <div><span>Counted</span><strong>{summary.counted_total}</strong></div>
              <div>
                <span>Variance</span>
                <strong style={{ color: summary.variance_total !== 0 ? "#ef4444" : "#22c55e" }}>
                  {summary.variance_total >= 0 ? `+${summary.variance_total}` : summary.variance_total}
                </strong>
              </div>
            </>
          )}
        </div>

        {/* ── Scan bar (only when session is active) ── */}
        {!isFinished && (
          <form className="stkview-scanbar" onSubmit={handleScan}>
            <input
              ref={scanInputRef}
              type="text"
              className="stkview-scaninput"
              placeholder="Scan or type serial number…"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              autoFocus
            />
            <button type="submit" className="stkview-scanbtn">Record Scan</button>
            <button
              type="button"
              className="stkview-completebtn"
              onClick={handleComplete}
              disabled={completing}
            >
              {completing ? "Completing…" : "Complete Stocktake"}
            </button>
          </form>
        )}
        {scanSuccess   && <p className="stkview-feedback stkview-feedback--ok">{scanSuccess}</p>}
        {scanError     && <p className="stkview-feedback stkview-feedback--err">{scanError}</p>}
        {completeError && <p className="stkview-feedback stkview-feedback--err">{completeError}</p>}

        {/* ── Filters ── */}
        <div className="stkview-filters">
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
          >
            <option value="">All Locations</option>
            {locations.map((l) => (
              <option key={l.location_id} value={l.location_id}>{l.location_name}</option>
            ))}
          </select>

          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            <option value="PO">PO</option>
            <option value="FOC">FOC</option>
          </select>
        </div>

        {/* ── Lines table ── */}
        <div className="UserList-container">
          {displayLines.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Model Name</th>
                  <th>IMEI</th>
                  <th>Serial Number</th>
                  <th>Expected Location</th>
                  <th>Type</th>
                  <th>Variance</th>
                </tr>
              </thead>
              <tbody>
                {displayLines.map((row) => {
                  const v = getVarianceDisplay(row);
                  const isOutlier = isFinished && v.value !== 0;
                  return (
                    <tr key={row.serial_number} className={isOutlier ? "row-outlier" : ""}>
                      <td>{row.model || "—"}</td>
                      <td>{row.imei_number || "—"}</td>
                      <td>{row.serial_number}</td>
                      <td>{row.location_name || "—"}</td>
                      <td>{row.inventory_type || "—"}</td>
                      <td>
                        <span className={`variance-badge ${v.cls}`}>{v.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="stkview-empty">No lines match the current filters.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewStocktakeForm;